import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import {
  DataTableFilterMeta,
  DataTablePageEvent,
  DataTableFilterEvent,
} from 'primereact/datatable';
import { getPaginatedAPIFDFData } from 'services/productWikiServices';
import {
  APIFDFImportData,
  APIFDFExportData,
  APIFDFPaginationState,
} from 'Interfaces/ProductWikiInterfaces/apifdf.types';
import { UseAPIFDFSectionReturn, APIFDFSectionProps } from './APIFDFSection.types';

const DEFAULT_PAGE_SIZE = 10;
const FILTER_DEBOUNCE_MS = 300;

// Initial filter configurations
const createInitialImportFilters = (): DataTableFilterMeta => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  importer_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  supplier_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  apifdf_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  unit_price: { value: null, matchMode: FilterMatchMode.CONTAINS },
  quantity: { value: null, matchMode: FilterMatchMode.CONTAINS },
  duty: { value: null, matchMode: FilterMatchMode.CONTAINS },
  supplier_address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  importer_address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  importer_city_state: { value: null, matchMode: FilterMatchMode.CONTAINS },
  import_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const createInitialExportFilters = (): DataTableFilterMeta => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  exporter_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  exporter_address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  exporter_city_address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  exporter_country: { value: null, matchMode: FilterMatchMode.CONTAINS },
  apifdf_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  unit_price: { value: null, matchMode: FilterMatchMode.CONTAINS },
  quantity: { value: null, matchMode: FilterMatchMode.CONTAINS },
  consignee_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  export_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const createInitialPaginationState = (): APIFDFPaginationState => ({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalRecords: 0,
  first: 0,
});

/**
 * Format a Date object to ISO date string (YYYY-MM-DD)
 */
const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert PrimeReact DataTableFilterMeta to a simple key-value object for API
 */
const convertFiltersToApiFormat = (filters: DataTableFilterMeta): Record<string, string> => {
  const apiFilters: Record<string, string> = {};

  Object.entries(filters).forEach(([field, filterMeta]) => {
    // Skip global filter and ensure filterMeta has a value
    if (field === 'global') return;

    // Handle both single filter and array of filters
    const meta = Array.isArray(filterMeta) ? filterMeta[0] : filterMeta;
    if (meta && 'value' in meta && meta.value !== null && meta.value !== '') {
      // Handle date range filters (array of [from, to])
      if (Array.isArray(meta.value)) {
        const [from, to] = meta.value;
        if (from instanceof Date) {
          apiFilters[`${field}_from`] = formatDateToISO(from);
        }
        if (to instanceof Date) {
          apiFilters[`${field}_to`] = formatDateToISO(to);
        }
      } else {
        apiFilters[field] = String(meta.value);
      }
    }
  });

  return apiFilters;
};

const useAPIFDFSection = ({ initialData }: APIFDFSectionProps): UseAPIFDFSectionReturn => {
  // Get molecule and strength from URL params for filtering APIFDF data
  // Support both 'molecule' (explicit) and 'item' (backward compatibility) parameters
  const [searchParams] = useSearchParams();
  const molecule = searchParams.get('molecule') || searchParams.get('item') || undefined;
  const strength = searchParams.get('strength') || undefined;

  // Data states
  const [importData, setImportData] = useState<APIFDFImportData[]>(initialData?.import || []);
  const [exportData, setExportData] = useState<APIFDFExportData[]>(initialData?.export || []);

  // Loading states (separate for each tab)
  const [isLoading, setIsLoading] = useState<boolean>(!initialData);
  const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Tab management
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  // Filter states
  const [importFilters, setImportFilters] = useState<DataTableFilterMeta>(
    createInitialImportFilters()
  );
  const [exportFilters, setExportFilters] = useState<DataTableFilterMeta>(
    createInitialExportFilters()
  );

  // Pagination states
  const [importPagination, setImportPagination] = useState<APIFDFPaginationState>(
    createInitialPaginationState()
  );
  const [exportPagination, setExportPagination] = useState<APIFDFPaginationState>(
    createInitialPaginationState()
  );

  // Refs for debouncing and abort controllers
  const importFilterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const exportFilterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Fetch paginated data for a specific type
   */
  const fetchPaginatedData = useCallback(
    async (
      type: 'import' | 'export',
      page: number,
      pageSize: number,
      filters: DataTableFilterMeta
    ) => {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const setLoadingState = type === 'import' ? setIsImportLoading : setIsExportLoading;
      const setData = type === 'import' ? setImportData : setExportData;
      const setPagination = type === 'import' ? setImportPagination : setExportPagination;

      try {
        setLoadingState(true);
        setError(null);

        const apiFilters = convertFiltersToApiFormat(filters);

        const response = await getPaginatedAPIFDFData(
          {
            type,
            page,
            pageSize,
            filters: apiFilters,
            molecule, // Filter by molecule name in description
            strength, // Filter by strength in description
          },
          abortControllerRef.current.signal
        );

        if (response.data) {
          setData(response.data.data as APIFDFImportData[] & APIFDFExportData[]);
          setPagination(prev => ({
            ...prev,
            page,
            pageSize,
            totalRecords: response.data.totalRecords,
            first: (page - 1) * pageSize,
          }));
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (err instanceof Error && err.name === 'CanceledError') {
          return;
        }
        console.error(`Error fetching APIFDF ${type} data:`, err);
        setError(`Failed to load APIFDF ${type} data. Please try again later.`);
      } finally {
        setLoadingState(false);
      }
    },
    [molecule, strength]
  );

  /**
   * Handle page change for Import DataTable
   */
  const onImportPageChange = useCallback(
    (event: DataTablePageEvent) => {
      const newPage = Math.floor(event.first / event.rows) + 1;
      fetchPaginatedData('import', newPage, event.rows, importFilters);
    },
    [fetchPaginatedData, importFilters]
  );

  /**
   * Handle page change for Export DataTable
   */
  const onExportPageChange = useCallback(
    (event: DataTablePageEvent) => {
      const newPage = Math.floor(event.first / event.rows) + 1;
      fetchPaginatedData('export', newPage, event.rows, exportFilters);
    },
    [fetchPaginatedData, exportFilters]
  );

  /**
   * Handle filter change for Import DataTable (debounced)
   */
  const onImportFilterChange = useCallback(
    (event: DataTableFilterEvent) => {
      setImportFilters(event.filters);

      // Clear existing debounce timer
      if (importFilterDebounceRef.current) {
        clearTimeout(importFilterDebounceRef.current);
      }

      // Debounce the API call
      importFilterDebounceRef.current = setTimeout(() => {
        // Reset to first page when filtering
        fetchPaginatedData('import', 1, importPagination.pageSize, event.filters);
      }, FILTER_DEBOUNCE_MS);
    },
    [fetchPaginatedData, importPagination.pageSize]
  );

  /**
   * Handle filter change for Export DataTable (debounced)
   */
  const onExportFilterChange = useCallback(
    (event: DataTableFilterEvent) => {
      setExportFilters(event.filters);

      // Clear existing debounce timer
      if (exportFilterDebounceRef.current) {
        clearTimeout(exportFilterDebounceRef.current);
      }

      // Debounce the API call
      exportFilterDebounceRef.current = setTimeout(() => {
        // Reset to first page when filtering
        fetchPaginatedData('export', 1, exportPagination.pageSize, event.filters);
      }, FILTER_DEBOUNCE_MS);
    },
    [fetchPaginatedData, exportPagination.pageSize]
  );

  // Initial data fetch on mount and when concept name changes
  useEffect(() => {
    // If initial data is provided (legacy support), use it and skip fetching
    if (initialData) {
      setImportPagination(prev => ({
        ...prev,
        totalRecords: initialData.import?.length || 0,
      }));
      setExportPagination(prev => ({
        ...prev,
        totalRecords: initialData.export?.length || 0,
      }));
      setIsLoading(false);
      return;
    }

    // Fetch initial data for both tabs (filtered by concept name)
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch import data (active tab)
        await fetchPaginatedData('import', 1, DEFAULT_PAGE_SIZE, importFilters);
        // Prefetch export data
        await fetchPaginatedData('export', 1, DEFAULT_PAGE_SIZE, exportFilters);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (importFilterDebounceRef.current) {
        clearTimeout(importFilterDebounceRef.current);
      }
      if (exportFilterDebounceRef.current) {
        clearTimeout(exportFilterDebounceRef.current);
      }
    };
    // Re-fetch when molecule or strength changes (user navigates to different product)
    // Note: fetchPaginatedData, importFilters, and exportFilters are intentionally excluded
    // to prevent infinite loops - they only need to run on initial mount or when search params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [molecule, strength]);

  return {
    // Data
    importData,
    exportData,

    // Loading states
    isLoading,
    isImportLoading,
    isExportLoading,
    error,

    // Tab management
    activeTabIndex,
    setActiveTabIndex,

    // Filters
    importFilters,
    exportFilters,

    // Pagination states
    importPagination,
    exportPagination,

    // Event handlers
    onImportPageChange,
    onExportPageChange,
    onImportFilterChange,
    onExportFilterChange,
  };
};

export default useAPIFDFSection;
